import { useCallback, useState } from 'react';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { MatterDocsWithCasesFilters } from '../../../../../../../backend/src/matter/doc/dto/doc-with-cases.response.dto';
import { DocsTable } from '../docs/components/docs-table';
import { APIBaseChronos } from '@/api/hosts';
import { useCreateDocumentCsvExport, useDownloadDocumentCsvExport } from '@/api/mutations/useDownloadMatterDocsCsv';
import { useGetMatterDocsWithCases } from '@/api/queries/useGetMatterDocsWithCases';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission, PermissionGate } from '@/hooks/useHasPermission';
import { GenericExportProgressModal } from '@/new/components/dialogs/export-progress-dialog';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { FilterPill } from '@/utils/buildFilterPills';

export function DocumentManagement() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'file.name', desc: false }]);
  const [filters, setFilters] = useState<MatterDocsWithCasesFilters>(() => {
    // Initialize filters from URL params
    const processedParam = searchParams.get('processed');
    if (processedParam === 'true') return { processed: true };
    if (processedParam === 'false') return { processed: false };
    return {};
  });

  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const debouncedSetSearchQuery = useDebouncedCallback((value: string) => {
    setDebouncedSearchQuery(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, 300);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setLocalSearchQuery(query);
      debouncedSetSearchQuery(query);
    },
    [debouncedSetSearchQuery],
  );

  const handleFiltersChange = useCallback(
    (newFilters: MatterDocsWithCasesFilters) => {
      setFilters(newFilters);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));

      // Sync processed filter with URL
      if (newFilters.processed === true) {
        setSearchParams({ processed: 'true' });
      } else if (newFilters.processed === false) {
        setSearchParams({ processed: 'false' });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );

  const handleRemoveFilter = useCallback(
    (filterKey: string) => {
      setFilters((prev) => {
        const updated = { ...prev };
        delete updated[filterKey as keyof MatterDocsWithCasesFilters];
        return updated;
      });
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));

      // Clear URL param when removing processed filter
      if (filterKey === 'processed') {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSearchParams({});
  }, [setSearchParams]);

  // Build filter pills
  const filterPills: FilterPill[] = [];
  if (filters.processed === true) {
    filterPills.push({
      id: 'processed-true',
      label: 'Processed',
      color: CHIP_COLOURS.default,
      onRemove: () => handleRemoveFilter('processed'),
    });
  } else if (filters.processed === false) {
    filterPills.push({
      id: 'processed-false',
      label: 'Not Processed',
      color: CHIP_COLOURS.default,
      onRemove: () => handleRemoveFilter('processed'),
    });
  }

  const handleSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    setSorting(updaterOrValue);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Convert sorting state to API params
  const sortBy = sorting.length > 0 ? (sorting?.[0]?.id?.split('.').pop() ?? undefined) : undefined;
  const sortOrder = sorting.length > 0 ? (sorting?.[0]?.desc ? 'DESC' : 'ASC') : undefined;

  const { data: matter } = useGetMatterV2(matterId);

  // User can delete documents if they are the matter creator OR have the DocumentDelete permission
  const isMatterCreator = matter?.createdById === user?.id;
  const canDeleteDocuments = isMatterCreator || hasPermission(Permission.DocumentDelete);

  // CSV export state
  const [csvExportModalOpen, setCsvExportModalOpen] = useState(false);
  const [csvExportId, setCsvExportId] = useState<string | null>(null);
  const [isCsvExportCreating, setIsCsvExportCreating] = useState(false);
  const [csvExportError, setCsvExportError] = useState<string>();

  const { mutate: createCsvExport } = useCreateDocumentCsvExport(matterId);
  const { mutate: downloadCsvExport } = useDownloadDocumentCsvExport(matterId);

  const handleStartCsvExport = useCallback(() => {
    // If an export is already in progress, reopen the modal
    if (csvExportId) {
      setCsvExportModalOpen(true);
      return;
    }

    createCsvExport(undefined, {
      onSuccess: (data) => {
        setCsvExportError(undefined);
        setIsCsvExportCreating(true);
        setCsvExportModalOpen(true);
        setCsvExportId(data.exportId);
      },
      onError: (error) => {
        setCsvExportError(error instanceof Error ? error.message : 'Failed to start CSV export');
        setCsvExportModalOpen(true);
      },
    });
  }, [createCsvExport, csvExportId]);

  const handleCsvExportDownload = useCallback(async () => {
    if (!csvExportId) return;
    downloadCsvExport(csvExportId, {
      onSuccess: () => {
        setCsvExportModalOpen(false);
        setIsCsvExportCreating(false);
        setCsvExportId(null);
      },
    });
  }, [csvExportId, downloadCsvExport]);

  const { data, isLoading } = useGetMatterDocsWithCases(matterId, {
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters: { ...filters, searchQuery: debouncedSearchQuery || undefined },
    sortBy,
    sortOrder,
  });

  return (
    <>
      <SettingsHeader
        title="Document Management"
        subheading={
          <p className="text-sm text-gray-500">
            View and manage all documents uploaded to {matter?.name ?? 'this matter'}.
          </p>
        }
        action={
          <PermissionGate permission={Permission.DocumentUpload} uiMode="disabled">
            <Button asChild variant="primary">
              <Link to={`/matters/${matterId}/docs/upload`}>
                <Icon className="lucide--file-up" />
                <span>Upload Documents</span>
              </Link>
            </Button>
          </PermissionGate>
        }
      />

      <DocsTable
        docs={data?.docs}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageCount={data?.pageCount ?? 0}
        totalCount={data?.totalCount ?? 0}
        searchQuery={localSearchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filterPills={
          filterPills.length > 0 ? <FilterPills pills={filterPills} onClearAll={handleClearAllFilters} /> : undefined
        }
        sorting={sorting}
        onSortingChange={handleSortingChange}
        canDeleteDocuments={canDeleteDocuments}
        onDownloadCsv={handleStartCsvExport}
        isDownloadingCsv={isCsvExportCreating}
      />

      <GenericExportProgressModal
        isOpen={csvExportModalOpen}
        onClose={() => {
          setCsvExportModalOpen(false);
          setIsCsvExportCreating(false);
          setCsvExportError(undefined);
        }}
        exportConfig={{
          progressEndpoint: `${APIBaseChronos}/api/storage/document-csv-export-events/${matterId}`,
          exportId: csvExportId,
        }}
        itemName="Documents CSV"
        onDownload={handleCsvExportDownload}
        isCreating={isCsvExportCreating}
        error={csvExportError}
        readyDescription={`Your documents list for "${matter?.name ?? 'this matter'}" is ready to download as a CSV.`}
        downloadButtonText="Download Documents.csv"
        onRetry={() => {
          setCsvExportId(null);
          setCsvExportError(undefined);
          handleStartCsvExport();
        }}
      />
    </>
  );
}
